'use client';

import classNames from 'classnames';
import {
  CSSProperties,
  ElementType,
  FC,
  FocusEvent,
  KeyboardEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Country, getCountryCallingCode } from 'react-phone-number-input/max';

import { useTranslation } from '@/hooks/useTranslations';

interface CountryOption {
  value?: Country;
  label: string;
  divider?: boolean;
}

const PANEL_GAP = 8;
const PANEL_MAX_HEIGHT = 360;
const VIEWPORT_PADDING = 16;

interface CountrySelectProps {
  value?: Country;
  onChange: (country?: Country) => void;
  options: CountryOption[];
  iconComponent: ElementType;
  onFocus?: (event: FocusEvent<HTMLElement>) => void;
  onBlur?: (event: FocusEvent<HTMLElement>) => void;
  disabled?: boolean;
  readOnly?: boolean;
  'aria-label'?: string;
}

const CountrySelect: FC<CountrySelectProps> = ({
  value,
  onChange,
  options,
  iconComponent: Flag,
  onFocus,
  onBlur,
  disabled,
  readOnly,
  'aria-label': ariaLabel,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelStyle, setPanelStyle] = useState<CSSProperties>();
  const [portalTarget, setPortalTarget] = useState<Element | null>(null);
  const shouldCenterRef = useRef(false);

  const countries = useMemo(
    () =>
      options
        .filter((option): option is Required<CountryOption> => !option.divider && !!option.value)
        .map((option) => ({ ...option, code: `+${getCountryCallingCode(option.value)}` })),
    [options]
  );

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return countries;
    }
    const digits = query.replace(/\D/g, '');
    return countries.filter(
      ({ label, value: country, code }) =>
        label.toLowerCase().includes(query) ||
        country.toLowerCase() === query ||
        (!!digits && code.slice(1).startsWith(digits))
    );
  }, [countries, search]);

  const selected = countries.find((country) => country.value === value);

  const close = (focusButton = false) => {
    setIsOpen(false);
    setSearch('');
    if (focusButton) {
      buttonRef.current?.focus();
    }
  };

  const open = () => {
    if (disabled || readOnly) {
      return;
    }
    setActiveIndex(
      Math.max(
        0,
        countries.findIndex((country) => country.value === value)
      )
    );
    setPortalTarget(rootRef.current?.closest('#modal-container') ?? document.body);
    shouldCenterRef.current = true;
    setIsOpen(true);
  };

  const select = (country: Country) => {
    close();
    onChange(country);
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target) && !panelRef.current?.contains(target)) {
        close();
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }
    const updatePosition = () => {
      const anchor = rootRef.current?.closest('.PhoneInput') ?? rootRef.current;
      if (!anchor) {
        return;
      }
      const rect = anchor.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - PANEL_GAP - VIEWPORT_PADDING;
      const spaceAbove = rect.top - PANEL_GAP - VIEWPORT_PADDING;
      const openBelow = spaceBelow >= PANEL_MAX_HEIGHT || spaceBelow >= spaceAbove;

      setPanelStyle({
        left: rect.left,
        width: rect.width,
        maxHeight: Math.min(PANEL_MAX_HEIGHT, openBelow ? spaceBelow : spaceAbove),
        ...(openBelow
          ? { top: rect.bottom + PANEL_GAP }
          : { bottom: window.innerHeight - rect.top + PANEL_GAP }),
      });
    };

    const handleScroll = (event: Event) => {
      if (!panelRef.current?.contains(event.target as Node)) {
        updatePosition();
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  useEffect(() => {
    const list = listRef.current;
    const item = list?.children[activeIndex] as HTMLElement | undefined;
    if (!list || !item) {
      return;
    }
    if (item.offsetTop < list.scrollTop) {
      list.scrollTop = item.offsetTop;
    } else if (item.offsetTop + item.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = item.offsetTop + item.offsetHeight - list.clientHeight;
    }
  }, [activeIndex]);

  useEffect(() => {
    const list = listRef.current;
    const item = list?.querySelector<HTMLElement>('.isSelected');
    if (!shouldCenterRef.current || !panelStyle || !list) {
      return;
    }
    shouldCenterRef.current = false;
    if (item) {
      list.scrollTop = item.offsetTop - (list.clientHeight - item.offsetHeight) / 2;
    }
  }, [panelStyle]);

  const handleSearchChange = (text: string) => {
    setSearch(text);
    setActiveIndex(0);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isOpen) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        open();
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((index) => Math.min(index + 1, filtered.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((index) => Math.max(index - 1, 0));
        break;
      case 'Enter':
        event.preventDefault();
        if (filtered[activeIndex]) {
          select(filtered[activeIndex].value);
        }
        break;
      case 'Escape':
        event.preventDefault();
        close(true);
        break;
      case 'Tab':
        close();
        break;
    }
  };

  return (
    <div
      ref={rootRef}
      className={classNames('PhoneInputCountry', 'popOkCountrySelect', { isOpen })}
      onKeyDown={handleKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      <button
        ref={buttonRef}
        type="button"
        className="popOkCountrySelectButton"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        disabled={disabled || readOnly}
        onClick={() => (isOpen ? close() : open())}
      >
        {selected && <Flag country={selected.value} label={selected.label} aria-hidden />}
        <span className="PhoneInputCountrySelectArrow" />
      </button>

      {isOpen &&
        portalTarget &&
        createPortal(
          <div
            ref={panelRef}
            className="popOkCountrySelectPanel"
            style={panelStyle}
            onClick={(event) => event.stopPropagation()}
          >
            <input
              autoFocus
              type="text"
              className="popOkCountrySelectSearch"
              value={search}
              placeholder={t('Games-All-Search')}
              onChange={(event) => handleSearchChange(event.target.value)}
            />
            <ul
              ref={listRef}
              role="listbox"
              aria-label={ariaLabel}
              className="popOkCountrySelectList"
            >
              {filtered.map((country, index) => (
                <li
                  key={country.value}
                  role="option"
                  aria-selected={country.value === value}
                  className={classNames('popOkCountrySelectOption', {
                    isActive: index === activeIndex,
                    isSelected: country.value === value,
                  })}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => select(country.value)}
                >
                  <Flag country={country.value} label={country.label} aria-hidden />
                  <span className="popOkCountrySelectName">{country.label}</span>
                  <span className="popOkCountrySelectCode">{country.code}</span>
                </li>
              ))}
              {!filtered.length && <li className="popOkCountrySelectEmpty">—</li>}
            </ul>
          </div>,
          portalTarget
        )}
    </div>
  );
};

export default CountrySelect;
